import {
    Text,
    View,
    Image,
    StyleSheet,
} from '@react-pdf/renderer';
import moment from "moment";

import {
    NullableNumber,
    ReportItemType,
    ReportTimeRangeType,
} from "types/Reports";
import { REPORT_SENSOR_LIST } from 'constant/report';

import { getOverallUsageChartBase64PNG } from "../report/utils/reportChart";
import { formatValuesInReport } from '../report/utils/reportFormat';
import {
    averageByWithNull,
    getTotalAndPercentFromSeries,
    getAverageAndPercentFromSeries,
    getLowestHighestFromArray,
} from '../report/utils/reportArray';
import { getTitleByTime, getIndividualCardPrefix } from '../report/utils/reportString';

import ReportPDFOverallBulletText from './ReportPDFOverallBulletText';
import ReportPDFOverallEdgeText from './ReportPDFOverallEdgeText';
import ReportPDFOverallPercentText from './ReportPDFOverallPercentText';
import ReportPDFOverallAverageText from './ReportPDFOverallAverageText';
import ReportPDFOverallTotalText from './ReportPDFOverallTotalText';
import ReportPDFEmptyChart from './ReportPDFEmptyChart';

const individualItemStyles = StyleSheet.create({
    container: {
        position: 'relative',
        backgroundColor: '#F2F2F2',
        padding: 10,
    },
    title: {
        position: 'relative',
        fontSize: 12,
        fontFamily: 'Roboto',
        fontWeight: 500,
        color: '#333333',
    },
    chart: {
        position: 'relative',
        width: '100%',
        height: 125,
        marginTop: 10,
    },
});

type ReportPDFIndividualItemProps = {
    itemType: ReportItemType;
    timeRangeType: ReportTimeRangeType;
    itemName: string;

    colorHex: string;

    unit: string;

    data: NullableNumber[];
    prevData: NullableNumber[];

    categories: string[];
    time: moment.Moment;

    dateFormat: string;
    reportPeriod: string;

    isSecond: boolean;
};

export default function ReportPDFIndividualItem({
    itemType,
    timeRangeType,
    itemName,

    colorHex,

    unit,

    data,
    prevData,

    categories,
    time,

    dateFormat,
    reportPeriod,

    isSecond,
}: Readonly<ReportPDFIndividualItemProps>) {
    const titleTimeText = getTitleByTime(timeRangeType);

    let body: JSX.Element;
    let totalOrAvg: NullableNumber = null;

    if (data.length === 0) {
        body = <ReportPDFEmptyChart />;
    } else {
        let percent: NullableNumber;

        if ((REPORT_SENSOR_LIST as readonly ReportItemType[]).includes(itemType)) {
            const result = getAverageAndPercentFromSeries(data, prevData);
            percent = result.percent;
            totalOrAvg = result.avg;
        } else {
            const result = getTotalAndPercentFromSeries(data, prevData);
            percent = result.percent;
            totalOrAvg = result.total;
        }


        const average = averageByWithNull(data, (v) => v);
        const prevAverage = prevData.length > 0 ? averageByWithNull(prevData, (v) => v) : null;
        const lastDayOfMonth = moment.unix(time.unix()).endOf('month');

        // format the data then find the highest items and lowest items to display
        const displayData = formatValuesInReport(data);
        const { highestItem, lowestItem } = getLowestHighestFromArray(displayData, categories, lastDayOfMonth);

        const chartImageBase64: string = getOverallUsageChartBase64PNG({
            itemType,
            chartWidth: '1070px',
            chartHeight: '280px',
            x: categories,
            y: data,
            color: colorHex,
            backgroundColor: '#F2F2F2',
        });

        body = <>
            {
                percent === null || percent === 0 ? <></> :
                    <ReportPDFOverallPercentText
                        itemType={itemType}
                        percent={percent}
                    />
            }
            <ReportPDFOverallEdgeText
                isHighest={true}
                itemType={itemType}
                item={highestItem}
                unit={unit}
                dateFormat={dateFormat}
                numberOfItems={null}
            />
            {
                itemType !== 'waterLeakage' &&
                <ReportPDFOverallEdgeText
                    isHighest={false}
                    itemType={itemType}
                    item={lowestItem}
                    unit={unit}
                    dateFormat={dateFormat}
                    numberOfItems={null}
                />
            }
            {
                ['room', 'energy', 'water'].includes(itemType) &&
                <ReportPDFOverallAverageText
                    itemType={itemType}
                    average={average}
                    prevAverage={prevAverage}
                    unit={unit}
                    monthString={`in ${reportPeriod}`}
                />
            }

            <ReportPDFOverallBulletText itemType={itemType} />

            <Image style={individualItemStyles.chart} src={chartImageBase64} />
        </>;
    }

    const prefix = getIndividualCardPrefix(itemType);

    return <View style={{...individualItemStyles.container, marginTop: isSecond ? 5 : 0}}>
        <Text style={individualItemStyles.title}>{prefix}{itemName}{titleTimeText}</Text>

        <ReportPDFOverallTotalText
            itemType={itemType}
            numberOfItems={null}
            total={totalOrAvg}
            unit={unit}
            color={colorHex}
        />

        {body}
    </View>
};
